import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  superadminBillingService,
  type SaasPlan,
  type SaasPlanWrite,
} from '@/services/superadminBilling';
import { Layers, Plus, RefreshCw } from 'lucide-react';

const emptyForm: SaasPlanWrite = {
  slug: '',
  name: '',
  description: '',
  billingType: 'pay_as_you_go',
  currencyCode: 'EUR',
  monthlyAmountCents: undefined,
  isActive: true,
  sortOrder: 1,
};

const SuperadminSaasPlansPage: React.FC = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SaasPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SaasPlanWrite>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await superadminBillingService.listSaasPlans(false);
      setPlans(rows);
    } catch {
      toast({ title: 'Error', description: 'Failed to load SaaS plans', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (plan: SaasPlan) => {
    setEditingId(plan.id);
    setForm({
      slug: plan.slug,
      name: plan.name,
      description: plan.description ?? '',
      billingType: plan.billingType as SaasPlanWrite['billingType'],
      currencyCode: plan.currencyCode,
      monthlyAmountCents: plan.monthlyAmountCents,
      isActive: plan.isActive,
      sortOrder: plan.sortOrder,
    });
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: SaasPlanWrite = {
        ...form,
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        currencyCode: form.currencyCode.trim().toUpperCase(),
        monthlyAmountCents: form.monthlyAmountCents && form.monthlyAmountCents > 0
          ? form.monthlyAmountCents
          : undefined,
      };
      if (editingId) {
        await superadminBillingService.updateSaasPlan(editingId, payload);
        toast({ title: 'Updated', description: 'SaaS plan saved.' });
      } else {
        await superadminBillingService.createSaasPlan(payload);
        toast({ title: 'Created', description: 'SaaS plan added.' });
      }
      setShowForm(false);
      await load();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Could not save plan';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: string) => {
    try {
      await superadminBillingService.deactivateSaasPlan(id);
      toast({ title: 'Deactivated', description: 'Plan hidden from checkout.' });
      await load();
    } catch {
      toast({ title: 'Error', description: 'Could not deactivate plan', variant: 'destructive' });
    }
  };

  return (
    <>
      <Helmet>
        <title>SaaS Plans - Arrangelly Dashboard</title>
      </Helmet>
      <DashboardLayout>
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Layers className="w-5 h-5" />
                SaaS plans
              </CardTitle>
              <CardDescription>
                Teacher platform billing (PAYG or fixed monthly). Set monthly amount in cents for Rapyd
                checkout.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button size="sm" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add plan
                </Button>
              </div>

              {loading ? (
                <p className="text-gray-400 text-sm">Loading plans…</p>
              ) : plans.length === 0 ? (
                <p className="text-gray-400 text-sm">No plans yet. Add PAYG for staging tests.</p>
              ) : (
                <ul className="space-y-3">
                  {plans.map((p) => (
                    <li
                      key={p.id}
                      className="rounded-lg border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <div>
                        <p className="font-medium text-foreground">
                          {p.name}{' '}
                          <span className="text-xs text-slate-500">({p.slug})</span>
                          {!p.isActive && (
                            <span className="ml-2 text-xs text-amber-600">inactive</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {p.billingType} · {p.currencyCode}
                          {p.monthlyAmountCents != null ? ` · ${p.monthlyAmountCents}¢/mo` : ''}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                          Edit
                        </Button>
                        {p.isActive && (
                          <Button variant="outline" size="sm" onClick={() => void deactivate(p.id)}>
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {showForm && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 space-y-3 mt-4">
                  <h3 className="text-sm font-semibold text-foreground">
                    {editingId ? 'Edit plan' : 'New plan'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="text-xs text-slate-600">
                      Slug
                      <Input
                        value={form.slug}
                        onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                        placeholder="payg"
                      />
                    </label>
                    <label className="text-xs text-slate-600">
                      Name
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Pay as you go"
                      />
                    </label>
                    <label className="text-xs text-slate-600 sm:col-span-2">
                      Description
                      <Input
                        value={form.description ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      />
                    </label>
                    <label className="text-xs text-slate-600">
                      Billing type
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        value={form.billingType}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            billingType: e.target.value as SaasPlanWrite['billingType'],
                          }))
                        }
                      >
                        <option value="pay_as_you_go">pay_as_you_go</option>
                        <option value="fixed_monthly">fixed_monthly</option>
                      </select>
                    </label>
                    <label className="text-xs text-slate-600">
                      Currency
                      <Input
                        value={form.currencyCode}
                        onChange={(e) => setForm((f) => ({ ...f, currencyCode: e.target.value }))}
                        maxLength={3}
                      />
                    </label>
                    <label className="text-xs text-slate-600">
                      Monthly amount (cents, Rapyd checkout)
                      <Input
                        type="number"
                        value={form.monthlyAmountCents ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            monthlyAmountCents: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          }))
                        }
                      />
                    </label>
                    <label className="text-xs text-slate-600">
                      Sort order
                      <Input
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
                        }
                      />
                    </label>
                    <label className="text-xs text-slate-600 flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      />
                      Active
                    </label>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setShowForm(false)}>
                      Cancel
                    </Button>
                    <Button onClick={() => void save()} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SuperadminSaasPlansPage;
